/** Pure helpers for the media screens (unit-tested). */

import type { Media } from "@motolynk/api-client";
import type { MediaTile } from "@motolynk/ui";

export function formatBytes(bytes: number, locale = "en-IN"): string {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 || value >= 10 ? 0 : 1;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value)} ${units[unit]}`;
}

type RenditionMap = Record<
  string,
  { url: string | null; width: number; height: number } | undefined
>;

/** Best image URL for a size: the preset, else a larger one, else the original (public files only). */
export function imageUrl(
  item: Pick<Media, "renditions" | "url" | "kind">,
  preset: string,
): string | null {
  if (item.kind !== "image") return null;
  const renditions = item.renditions as unknown as RenditionMap;
  const order = ["thumbnail", "small", "medium", "large"];
  for (const name of order.slice(order.indexOf(preset))) {
    const url = renditions[name]?.url;
    if (url) return url;
  }
  return item.url;
}

export interface TileLabels {
  status: (status: string) => string;
  trash: string;
  pages: (count: number) => string;
  video: string;
}

export function toTile(item: Media, labels: TileLabels, locale = "en-IN"): MediaTile {
  const meta =
    item.kind === "image" && item.width && item.height
      ? `${item.width} × ${item.height} · ${formatBytes(item.size_bytes, locale)}`
      : item.kind === "document"
        ? [
            item.page_count ? labels.pages(item.page_count) : null,
            formatBytes(item.size_bytes, locale),
          ]
            .filter(Boolean)
            .join(" · ")
        : labels.video;
  let badge: MediaTile["badge"] = null;
  if (item.is_deleted) badge = { label: labels.trash, tone: "neutral" };
  else if (item.status === "failed") badge = { label: labels.status("failed"), tone: "danger" };
  else if (item.status !== "ready") badge = { label: labels.status(item.status), tone: "info" };
  return {
    id: item.uid,
    title: item.title || item.original_name,
    kind: item.kind,
    thumbnailUrl: imageUrl(item, "thumbnail"),
    badge,
    meta,
  };
}
